import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import CountryFlag from './CountryFlag';

// Per-row translated country name. Used inside the dropdown items + selected display
// so a language switch updates each visible row through its own selector subscription.
const CountryName = ({ country }) => {
    const label = useMasterLabel('masterCountry', country?.countryId, 'countryName', country?.countryName || '');
    return <>{label}</>;
};

/**
 * Single dropdown for country + timezone selection.
 * Each flat API entry {countryId, countryName, timeZone, flagImage} is one selectable option.
 * Display format: [flag] CountryName  -  TimeZone
 *
 * Props:
 * - countryTimezoneList: flat array from API [{countryId, countryName, timeZone, flagImage}, ...]
 * - countryId: selected country ID
 * - timeZone: selected timezone
 * - onChange: ({ countryId, timeZone, countryName }) => void
 * - placeholder: placeholder text
 * - disabled: disable the component
 * - error: show error state
 */
const CountryTimeZoneSelect = ({
    countryTimezoneList = [],
    countryId,
    timeZone,
    onChange,
    placeholder,
    disabled = false,
    error = false,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    // Find selected item from the flat list
    const selectedItem = countryTimezoneList.find(
        (item) => item.countryId === countryId && item.timeZone === timeZone
    ) || null;

    // Filter by search (matches country name or timezone)
    const filteredList = countryTimezoneList.filter((item) => {
        const q = search.toLowerCase();
        return (
            item.countryName?.toLowerCase().includes(q) ||
            item.timeZone?.toLowerCase().includes(q)
        );
    });

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Focus search when opened
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleSelect = (item) => {
        onChange({
            countryId: item.countryId,
            timeZone: item.timeZone,
            countryName: item.countryName,
        });
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange({ countryId: '', timeZone: '', countryName: '' });
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div ref={containerRef} className="relative" data-testid="country-timezone-select-container">
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 flex items-center justify-between gap-2 ${
                    error
                        ? 'border-red-500 bg-red-50'
                        : isOpen
                            ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/20 bg-white'
                            : 'border-gray-300 hover:border-[#4ABDBD] bg-white'
                } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
                data-testid="country-timezone-select-trigger-button"
            >
                <span className={`truncate flex items-center gap-2 ${!selectedItem ? 'text-gray-400' : 'text-gray-800'}`} data-testid="country-timezone-select-selected-text">
                    {selectedItem && (
                        <CountryFlag
                            countryName={selectedItem.countryName}
                            alt={selectedItem.countryName}
                            className="w-5 h-3.5 object-cover rounded-sm shrink-0"
                        />
                    )}
                    {selectedItem
                        ? <><CountryName country={selectedItem} />  -  {selectedItem.timeZone}</>
                        : (placeholder || t('common.select_country_timezone', 'Select Country & Timezone'))}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {selectedItem && !disabled && (
                        <span
                            onClick={handleClear}
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                            data-testid="country-timezone-select-clear-button"
                        >
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" data-testid="country-timezone-select-clear-icon" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-testid="country-timezone-select-chevron-icon" />
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 left-0 top-full mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150" data-testid="country-timezone-select-dropdown">
                    {/* Search Bar */}
                    <div className="p-2 border-b border-gray-100" data-testid="country-timezone-select-search-section">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="country-timezone-select-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('common.search_country', 'Search country...')}
                                className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                data-testid="country-timezone-select-search-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                                    data-testid="country-timezone-select-search-clear-button"
                                >
                                    <X className="w-3.5 h-3.5 text-gray-400" data-testid="country-timezone-select-search-clear-icon" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-48 overflow-y-auto custom-scrollbar" data-testid="country-timezone-select-options-list">
                        {filteredList.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-400" data-testid="country-timezone-select-no-results-text">
                                {t('common.no_countries_found', 'No countries found')}
                            </div>
                        ) : (
                            filteredList.map((item) => {
                                const isSelected = countryId === item.countryId && timeZone === item.timeZone;
                                return (
                                    <button
                                        key={`${item.countryId}-${item.timeZone}`}
                                        type="button"
                                        onClick={() => handleSelect(item)}
                                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-all duration-150 ${
                                            isSelected
                                                ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                        data-testid={`country-timezone-select-option-${item.countryId}-${item.timeZone}`}
                                    >
                                        <CountryFlag
                                            countryName={item.countryName}
                                            alt={item.countryName}
                                            className="w-5 h-3.5 object-cover rounded-sm shrink-0"
                                        />
                                        <span className="truncate flex-1 text-left">
                                            <CountryName country={item} />  <span className="text-gray-400 mx-1">-</span>  {item.timeZone}
                                        </span>
                                        {isSelected && (
                                            <Check className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" data-testid={`country-timezone-select-option-check-icon-${item.countryId}-${item.timeZone}`} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CountryTimeZoneSelect;
